// Studio data access: typed API calls (OpenAPI client) plus the multipart
// upload, which goes through fetch directly (same session cookie, CSRF and
// error normalization as every other call).

import { useQuery } from "@tanstack/react-query";
import type { AssetRef, AuthoringState, CreativeDocument } from "@interactads/creative-model";
import { api, call, csrf } from "@/api/client";
import { apiUrl } from "@/api/config";
import { ApiProblem, OFFLINE_ERROR, problemFrom } from "@/api/errors";
import type { components } from "@/api/schema.gen";
import type { CreativeVersion } from "@/api/types";

export type Asset = components["schemas"]["Asset"];
export type AssetRevision = components["schemas"]["AssetRevision"];

export const studioKeys = {
  assets: (orgId: string, brandId: string, kind: string) => ["org", orgId, "brand", brandId, "assets", kind] as const,
  asset: (orgId: string, assetId: string) => ["org", orgId, "asset", assetId] as const,
};

/**
 * The version as the Studio opens it: ALWAYS a fresh server read (state and
 * `editable` may have changed since another screen cached it), never cached
 * afterwards and never refetched in the background (an open editing session
 * is not reset by a refetch; the session's own saves carry the revision).
 */
export function useStudioVersion(orgId: string, versionId: string) {
  return useQuery({
    queryKey: ["studio", orgId, versionId],
    queryFn: () =>
      call(() => api.GET("/orgs/{orgId}/versions/{versionId}", { params: { path: { orgId, versionId } } })),
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export async function saveDocument(
  orgId: string,
  versionId: string,
  document: CreativeDocument,
  authoringState: AuthoringState,
  baseRevision: number
): Promise<CreativeVersion> {
  return call(() =>
    api.PUT("/orgs/{orgId}/versions/{versionId}/document", {
      params: { path: { orgId, versionId } },
      body: {
        document: document,
        authoringState: authoringState,
        baseRevision,
      },
    })
  );
}

/** Server-side flag of a new asset: "fallback_render" = an automatic fallback image (never in the library). */
export type UploadOrigin = "fallback_render" | "delivery_copy";

/** Uploads a file to the brand library (new asset) — type and size are read from the CONTENT by the server. */
export async function uploadAsset(
  orgId: string,
  brandId: string,
  file: File,
  name?: string,
  origin?: UploadOrigin
): Promise<Asset> {
  const form = new FormData();
  form.append("file", file);
  if (name) form.append("name", name);
  if (origin) form.append("origin", origin);
  let response: Response;
  try {
    response = await fetch(apiUrl(`/orgs/${orgId}/brands/${brandId}/assets`), {
      method: "POST",
      credentials: "include",
      headers: csrf.get() ? { "X-CSRF-Token": csrf.get() ?? "" } : {},
      body: form,
    });
  } catch {
    throw new ApiProblem(0, "NETWORK", OFFLINE_ERROR);
  }
  const body: unknown = await response.json().catch(() => undefined);
  if (!response.ok) throw problemFrom(response.status, body);
  return body as Asset;
}

export function useBrandAssets(
  orgId: string,
  brandId: string,
  kind: "image" | "font" | "video" | "audio",
  enabled = true
) {
  return useQuery({
    queryKey: studioKeys.assets(orgId, brandId, kind),
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/brands/{brandId}/assets", {
          params: { path: { orgId, brandId }, query: { kind, limit: 100 } },
        })
      ),
    // Automatic fallback renders are left out by the server (revision source derived_render).
    enabled: enabled && brandId !== "",
  });
}

export function useAsset(orgId: string, assetId: string | undefined) {
  return useQuery({
    queryKey: studioKeys.asset(orgId, assetId ?? ""),
    queryFn: () =>
      call(() => api.GET("/orgs/{orgId}/assets/{assetId}", { params: { path: { orgId, assetId: assetId ?? "" } } })),
    enabled: Boolean(assetId),
  });
}

/** What InteractAds AI may do with an asset (7.6F.1): brand governance; never changes its bytes. */
export async function setAssetAiUsage(orgId: string, assetId: string, aiUsage: Asset["aiUsage"]): Promise<Asset> {
  return call(() =>
    api.PATCH("/orgs/{orgId}/assets/{assetId}", { params: { path: { orgId, assetId } }, body: { aiUsage } })
  );
}

/** URL of an immutable revision's bytes (session cookie; cacheable forever). */
export const revisionUrl = (orgId: string, assetId: string, revisionId: string) =>
  apiUrl(`/orgs/${orgId}/assets/${assetId}/revisions/${revisionId}/content`);

export const assetUrlFor = (orgId: string) => (ref: Pick<AssetRef, "assetId" | "revisionId">) =>
  revisionUrl(orgId, ref.assetId, ref.revisionId);

/** Canonical pinned reference to an uploaded revision (metadata copied from the server, verified again on save). */
export function pinnedRef(asset: Asset, role: "content" | "fallback" | "font" | "poster"): Omit<AssetRef, "key"> {
  const r = asset.currentRevision;
  if (!r) throw new Error("asset without revision");
  return {
    assetId: asset.id,
    revisionId: r.id,
    sha256: r.sha256,
    kind: asset.kind,
    mime: r.mime,
    bytes: r.bytes,
    ...(r.width !== null ? { width: r.width } : {}),
    ...(r.height !== null ? { height: r.height } : {}),
    ...(r.hasAlpha !== null ? { hasAlpha: r.hasAlpha } : {}),
    ...(r.durationMs !== null ? { durationMs: r.durationMs } : {}),
    role,
  };
}
